import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import {
  Card,
  Spin,
  Alert,
  Tag,
  Typography,
  Button,
  Space,
  Input,
  Select,
  Row,
  Col,
  Empty
} from 'antd';
import {
  ShopOutlined,
  EnvironmentOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
  UnorderedListOutlined
} from '@ant-design/icons';
import partnerService from '../../services/partnerService';
import { getCityByLanguage, formatCityCountry } from '../../utils/locationFormat';
import 'leaflet/dist/leaflet.css';
import './PartnerMapPage.scss';

const { Title, Text, Paragraph } = Typography;
const { Search } = Input;
const { Option } = Select;

// Fix for default marker icons in Leaflet with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icon for verified partners
const verifiedIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Custom marker icon for impact partners
const impactIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-violet.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Default marker icon
const defaultIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const PartnerMapPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [cityFilter, setCityFilter] = useState(null);
  const [cities, setCities] = useState([]);

  // Greece center coordinates (default)
  const defaultCenter = [39.0742, 21.8243];
  const defaultZoom = 6;

  useEffect(() => {
    loadPartners();
  }, []);

  const loadPartners = async () => {
    setLoading(true);
    setError(null);

    const result = await partnerService.fetchPartners();

    if (result.success) {
      const partnersList = result.data.results || result.data || [];
      setPartners(partnersList);

      // Extract unique cities for filter
      const cityMap = new Map();
      partnersList.forEach(p => {
        if (p.city) {
          const translated = getCityByLanguage(p.city, i18n.language);
          const key = translated.toLowerCase();
          if (!cityMap.has(key)) {
            cityMap.set(key, { original: p.city, translated });
          }
        }
      });
      setCities([...cityMap.values()].sort((a, b) => a.translated.localeCompare(b.translated, i18n.language)));
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  // Filter partners and only include those with coordinates
  const partnersWithCoordinates = useMemo(() => {
    return partners.filter(partner => {
      const hasCoordinates = partner.latitude && partner.longitude;
      const matchesSearch = !searchQuery ||
        partner.store_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        partner.city?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCity = !cityFilter || partner.city === cityFilter;

      return hasCoordinates && matchesSearch && matchesCity;
    });
  }, [partners, searchQuery, cityFilter]);

  // Calculate map center based on partners
  const mapCenter = useMemo(() => {
    if (partnersWithCoordinates.length === 0) {
      return defaultCenter;
    }

    const lats = partnersWithCoordinates.map(p => parseFloat(p.latitude));
    const lngs = partnersWithCoordinates.map(p => parseFloat(p.longitude));

    const avgLat = lats.reduce((a, b) => a + b, 0) / lats.length;
    const avgLng = lngs.reduce((a, b) => a + b, 0) / lngs.length;

    return [avgLat, avgLng];
  }, [partnersWithCoordinates]);

  const getMarkerIcon = (partner) => {
    if (partner.is_impact_partner) {
      return impactIcon;
    }
    if (partner.is_verified) {
      return verifiedIcon;
    }
    return defaultIcon;
  };

  const handleViewPartner = (partnerId) => {
    navigate(`/partners/${partnerId}`);
  };

  if (loading) {
    return (
      <div className="partner-map-page">
        <div className="loading-container">
          <Spin size="large" />
        </div>
      </div>
    );
  }

  return (
    <div className="partner-map-page">
      <div className="page-header">
        <div className="header-content">
          <Title level={2}>
            <EnvironmentOutlined style={{ marginRight: 12 }} />
            {t('partnerMap.title')}
          </Title>
          <Paragraph className="page-description">
            {t('partnerMap.description')}
          </Paragraph>
        </div>
        <Button
          type="default"
          icon={<UnorderedListOutlined />}
          onClick={() => navigate('/partners')}
        >
          {t('partnerMap.viewList')}
        </Button>
      </div>

      {error && (
        <Alert
          message={t('common.error')}
          description={error}
          type="error"
          showIcon
          closable
          style={{ marginBottom: 24 }}
        />
      )}

      {/* Filters */}
      <Card className="filters-card">
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={10}>
            <Search
              placeholder={t('partners.searchPlaceholder')}
              allowClear
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              prefix={<SearchOutlined />}
              size="large"
            />
          </Col>
          <Col xs={24} md={8}>
            <Select
              placeholder={t('partners.filterByCity')}
              allowClear
              value={cityFilter}
              onChange={setCityFilter}
              style={{ width: '100%' }}
              size="large"
            >
              {cities.map(city => (
                <Option key={city.original} value={city.original}>{city.translated}</Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} md={6}>
            <div className="partner-count">
              <EnvironmentOutlined />
              <Text>
                {t('partnerMap.partnersOnMap', { count: partnersWithCoordinates.length })}
              </Text>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Legend */}
      <Card className="legend-card" size="small">
        <Space size="large">
          <span className="legend-item">
            <img
              src="https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png"
              alt="Standard"
              width={12}
              height={20}
            />
            <Text>{t('partnerMap.legend.standard')}</Text>
          </span>
          <span className="legend-item">
            <img
              src="https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png"
              alt="Verified"
              width={12}
              height={20}
            />
            <Text>{t('partnerMap.legend.verified')}</Text>
          </span>
          <span className="legend-item">
            <img
              src="https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-violet.png"
              alt="Impact"
              width={12}
              height={20}
            />
            <Text>{t('partnerMap.legend.impact')}</Text>
          </span>
        </Space>
      </Card>

      {/* Map */}
      {partnersWithCoordinates.length === 0 ? (
        <Card className="map-container">
          <Empty
            description={t('partnerMap.noPartnersWithLocation')}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </Card>
      ) : (
        <Card className="map-container" bodyStyle={{ padding: 0, height: '100%' }}>
          <MapContainer
            center={mapCenter}
            zoom={defaultZoom}
            style={{ height: '100%', width: '100%', minHeight: 500 }}
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {partnersWithCoordinates.map(partner => (
              <Marker
                key={partner.id}
                position={[parseFloat(partner.latitude), parseFloat(partner.longitude)]}
                icon={getMarkerIcon(partner)}
              >
                <Popup>
                  <div className="partner-popup">
                    <div className="popup-header">
                      {partner.logo ? (
                        <img
                          src={partner.logo}
                          alt={partner.store_name}
                          className="popup-logo"
                        />
                      ) : (
                        <div className="popup-logo-placeholder">
                          <ShopOutlined />
                        </div>
                      )}
                      <div className="popup-title">
                        <strong>{partner.store_name}</strong>
                        <div className="popup-tags">
                          {partner.is_verified && (
                            <Tag color="green" size="small">
                              <SafetyCertificateOutlined /> {t('partners.verified')}
                            </Tag>
                          )}
                          {partner.is_impact_partner && (
                            <Tag color="magenta" size="small">
                              🤝 {t('partners.impact')}
                            </Tag>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="popup-location">
                      <EnvironmentOutlined />
                      <span>{formatCityCountry(partner.city, partner.country, i18n.language)}</span>
                    </div>
                    {partner.service_categories && partner.service_categories.length > 0 && (
                      <div className="popup-categories">
                        {partner.service_categories.slice(0, 3).map((cat, idx) => (
                          <Tag key={idx} color="blue" size="small">
                            {i18n.language === 'el' ? (cat.name_el || cat.name_en) : cat.name_en}
                          </Tag>
                        ))}
                      </div>
                    )}
                    <Button
                      type="primary"
                      size="small"
                      block
                      onClick={() => handleViewPartner(partner.id)}
                      style={{ marginTop: 8 }}
                    >
                      {t('partners.viewProfile')}
                    </Button>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </Card>
      )}
    </div>
  );
};

export default PartnerMapPage;
